import { db } from '@/shared/db/database';
import type { Employee } from '../../domain/entities/Employee';
import type { EmployeeDTO, BiometricDataDTO } from '../dto/EmployeeDTO';
import { isCardBiometricData } from '../dto/EmployeeDTO';
import { toEmployee } from '../mappers/employeeMapper';
import { LocalFaceEmbeddingRepository } from '@/modules/attendance/infrastructure/repositories/LocalFaceEmbeddingRepository';
import type { AttendanceEmployee } from '@/modules/attendance/infrastructure/mappers/timelogMapper';
import { toAttendanceEmployeeFromEmployeeDTO } from '../mappers/attendanceEmployeeMapper';
import {
  TEMPLATE_CARD,
  TEMPLATE_CARD_ALT,
  TEMPLATE_FACE_LEGACY,
} from '../../domain/biometricConstants';

export type RemoveBiometricParams =
  | { kind: 'facial' }
  | { kind: 'nfc'; idEntityBiometric: number };

const localFaceEmbedding = new LocalFaceEmbeddingRepository();

export class LocalEmployeeRepository {
  async listByAccessRole(accessRoleId: number): Promise<Employee[]> {
    const rows = await db.employees.where('idAccessRole').equals(accessRoleId).toArray();
    const dtos = rows.map((row) => row.raw as EmployeeDTO);

    // Fire-and-forget: seed faceEmbeddings from cached raw.person_embed_v2 (offline path)
    void this._seedFaceEmbeddingsFromCache(accessRoleId, dtos);

    return dtos.map(toEmployee);
  }

  async findByEntity(idEntity: number): Promise<Employee | null> {
    const rows = await db.employees.toArray();
    const row = rows.find((r) => (r.raw as EmployeeDTO).id_entity === idEntity);
    return row ? toEmployee(row.raw as EmployeeDTO) : null;
  }

  async upsertAll(accessRoleId: number, dtos: EmployeeDTO[]): Promise<void> {
    const items = dtos.map((dto) => ({
      idPerson:    dto.id_person,
      idAccessRole: accessRoleId,
      syncStatus:  'synced' as const,
      updatedAt:   Date.now(),
      raw:         dto,
    }));
    await db.employees.bulkPut(items);
  }

  /** Returns how many employees are cached locally for a given access role. */
  async countByAccessRole(accessRoleId: number): Promise<number> {
    return db.employees.where('idAccessRole').equals(accessRoleId).count();
  }

  /**
   * Local-first lookup: returns the first employee whose raw.code or
   * raw.dui_person matches `value` (case-insensitive, trimmed).
   * Malformed rows (missing both fields) are silently skipped.
   */
  async findByCodeOrDui(accessRoleId: number, value: string): Promise<AttendanceEmployee | null> {
    const v = value.toLowerCase().trim();
    const rows = await db.employees.where('idAccessRole').equals(accessRoleId).toArray();
    for (const row of rows) {
      const raw = row.raw as EmployeeDTO;
      if (!raw || typeof raw !== 'object') continue;
      const code = typeof raw.code === 'string' ? raw.code.toLowerCase().trim() : '';
      const dui  = typeof raw.dui_person === 'string' ? raw.dui_person.toLowerCase().trim() : '';
      if (!code && !dui) continue; // malformed — skip
      if (code === v || dui === v) {
        return toAttendanceEmployeeFromEmployeeDTO(raw);
      }
    }
    return null;
  }

  /**
   * Local-first card lookup: returns the first employee whose biometric_data contains
   * a card entry (id_template_biometric 3 or 4) that matches `cardNumber`.
   *
   * Comparison order (HID readers emit the full decimal UID):
   *   1. decimal        — full UID string e.g. "43690627098" (primary match)
   *   2. decimal_short  — short decimal    e.g. "666666"     (secondary match)
   *   3. card_number    — 16-bit integer   e.g. 11306        (backward compat)
   *
   * Rows with malformed or non-card biometric_data entries are silently skipped.
   */
  async findByCardNumber(accessRoleId: number, cardNumber: string): Promise<AttendanceEmployee | null> {
    const rows = await db.employees.where('idAccessRole').equals(accessRoleId).toArray();
    for (const row of rows) {
      const dto = row.raw as EmployeeDTO;
      if (!dto || !Array.isArray(dto.biometric_data)) continue;
      for (const bd of dto.biometric_data) {
        // Only inspect card templates (3 = HID/RFID, 4 = alternate card format)
        if (bd.id_template_biometric !== TEMPLATE_CARD && bd.id_template_biometric !== TEMPLATE_CARD_ALT) continue;
        if (!isCardBiometricData(bd.biometric_data)) continue;
        const cd = bd.biometric_data;
        if (
          cd.decimal               === cardNumber ||
          String(cd.decimal_short) === cardNumber ||
          String(cd.card_number)   === cardNumber
        ) {
          return toAttendanceEmployeeFromEmployeeDTO(dto);
        }
      }
    }
    return null;
  }

  /**
   * Patches the biometric_data array of a locally-cached employee.
   *
   * Strategy:
   * - If an entry with the same `id_template_biometric` already exists → REPLACE it
   *   (re-enrollment: last write wins; matches server dedup behaviour).
   * - If no entry with that template exists → APPEND with nro_biometric = existing.length + 1.
   * - If biometric_data is absent/null → initialise to [] before appending.
   *
   * This is the write-side of the offline-first enrollment flow:
   * called after the remote API succeeds or by enrollBiometricLocallyUseCase offline.
   */
  async patchBiometricData(idPerson: number, entry: BiometricDataDTO): Promise<void> {
    const row = await db.employees.get(idPerson);
    if (!row) return;

    const raw = row.raw as EmployeeDTO;
    const existing: BiometricDataDTO[] = Array.isArray(raw.biometric_data) ? raw.biometric_data : [];

    const existingIdx = existing.findIndex(
      (e) => e.id_template_biometric === entry.id_template_biometric,
    );

    let nextBiometricData: BiometricDataDTO[];
    if (existingIdx >= 0) {
      // Re-enrollment: replace the existing entry, preserve its nro_biometric
      nextBiometricData = existing.map((e, i) =>
        i === existingIdx ? { ...entry, nro_biometric: e.nro_biometric } : e,
      );
    } else {
      // First enrollment: append with next sequence number
      nextBiometricData = [...existing, { ...entry, nro_biometric: existing.length + 1 }];
    }

    const updated: EmployeeDTO = { ...raw, biometric_data: nextBiometricData };
    await db.employees.update(idPerson, { raw: updated });
  }

  /**
   * Removes a biometric enrollment from the locally-cached employee row.
   *
   * Strategy:
   * - kind:'facial' → filter out all entries with id_template_biometric === 14
   *                   + set person_embed_v2 to null.
   * - kind:'nfc'    → filter out the entry whose id_entity_biometric matches.
   *
   * If the employee row is not found, resolves silently (no-op).
   *
   * Change: nueva-seccion-de-borrado-perfiles-en-administracion-empleados
   */
  async removeBiometricData(idPerson: number, params: RemoveBiometricParams): Promise<void> {
    const row = await db.employees.get(idPerson);
    if (!row) return;

    const raw = row.raw as EmployeeDTO;
    const existing: BiometricDataDTO[] = Array.isArray(raw.biometric_data) ? raw.biometric_data : [];

    let nextBiometricData: BiometricDataDTO[];
    // EmployeeDTO.person_embed_v2 is optional (number[] | null | undefined). Coerce
    // missing values to null so the assignment satisfies exactOptionalPropertyTypes.
    let nextEmbedV2: number[] | null = raw.person_embed_v2 ?? null;

    if (params.kind === 'facial') {
      // Defensive housekeeping: filter legacy template-14 entries even though the
      // app's source of truth for facial enrollment is `person_embed_v2`. Keeps
      // the local cache consistent with what the backend would return after delete.
      nextBiometricData = existing.filter((e) => e.id_template_biometric !== TEMPLATE_FACE_LEGACY);
      nextEmbedV2 = null;
    } else {
      nextBiometricData = existing.filter((e) => e.id_entity_biometric !== params.idEntityBiometric);
    }

    const updated: EmployeeDTO = {
      ...raw,
      biometric_data: nextBiometricData,
      person_embed_v2: nextEmbedV2,
    };
    await db.employees.update(idPerson, { raw: updated });
  }

  /**
   * Returns the id_entity_biometric for the first card (NFC/RFID) entry found in
   * the locally-cached employee row (template !== 14 && !== 5), or null if absent.
   *
   * Reads raw Dexie row directly — mapper is NOT used (per design decision).
   *
   * Change: nueva-seccion-de-borrado-perfiles-en-administracion-empleados
   */
  async getNfcBiometricId(idPerson: number): Promise<number | null> {
    const row = await db.employees.get(idPerson);
    if (!row) return null;

    const raw = row.raw as EmployeeDTO;
    if (!Array.isArray(raw.biometric_data)) return null;

    for (const entry of raw.biometric_data) {
      // Only card templates carry an id_entity_biometric we can target for delete.
      if (entry.id_template_biometric !== TEMPLATE_CARD && entry.id_template_biometric !== TEMPLATE_CARD_ALT) continue;
      if (typeof entry.id_entity_biometric === 'number') {
        return entry.id_entity_biometric;
      }
    }

    return null;
  }

  /**
   * Sets `raw.person_embed_v2` on the locally-cached employee row to the given
   * 512-d vector. Used by the offline-first enrollment flow so that — after a
   * fresh facial enrollment — `toEmployee(raw).enrolledMethods.face` correctly
   * reflects the new state even before the sync queue drains to the backend.
   *
   * If the employee row is not found, resolves silently (no-op). This can
   * happen when an employee is enrolled before being fully synced into the
   * local employees cache (rare edge case).
   */
  async patchPersonEmbedV2(idPerson: number, embedding: number[]): Promise<void> {
    const row = await db.employees.get(idPerson);
    if (!row) return;
    const raw = row.raw as EmployeeDTO;
    const updated: EmployeeDTO = { ...raw, person_embed_v2: embedding };
    await db.employees.update(idPerson, { raw: updated });
  }

  /**
   * Patch the photo_rekognition field for an employee in the local cache.
   * Used after enrolling a new face to immediately reflect the captured photo in the UI.
   */
  async patchPhoto(idPerson: number, photoFileName: string): Promise<void> {
    const row = await db.employees.get(idPerson);
    if (!row) return;
    const raw = row.raw as EmployeeDTO;
    const updated: EmployeeDTO = { ...raw, photo_rekognition: photoFileName };
    await db.employees.update(idPerson, { raw: updated });
  }

  /** Fire-and-forget helper: seeds faceEmbeddings from cached employee DTOs. */
  private async _seedFaceEmbeddingsFromCache(
    accessRoleId: number,
    dtos: EmployeeDTO[],
  ): Promise<void> {
    try {
      const candidates = dtos
        .filter(
          (d) =>
            Array.isArray(d.person_embed_v2) &&
            d.person_embed_v2.length === 512 &&
            d.person_embed_v2.every(Number.isFinite),
        )
        .map((d) => ({ idPerson: d.id_person, vector: d.person_embed_v2! }));

      if (candidates.length === 0) return;

      await localFaceEmbedding.bulkSeedFromRemote(accessRoleId, candidates);
    } catch (err) {
      console.warn('[LocalEmployeeRepository] faceEmbedding offline seed failed (non-fatal):', err);
    }
  }
}
